import React, { useEffect, useState } from 'react';
import { HQ } from './sportConfig';

// Thin React wrappers around the shared <shq-follow> and <shq-follow-marker>
// elements (gateway/follows.js). The elements own state, storage, toast and undo.
// Placement, size and labels are decided by the callers and by gateway/follows.css.

export const startOf = (match) => match?.starts_at || match?.match_time
  || (match?.date && match?.time ? `${match.date}T${match.time}` : "");

export const gameAttributes = (match, sport = HQ.sport) => ({
  sport,
  link: match?.match_link || "",
  home: match?.home_team || "",
  away: match?.away_team || "",
  start: startOf(match),
  league: match?.league || match?.competition_name || "",
});

// Star for one game (beside the match page heading).
export function GameFollow({ match, link, sport = HQ.sport, className }) {
  const attributes = gameAttributes(match, sport);
  if (link) attributes.link = link;
  return <shq-follow type="game" class={className} {...attributes} />;
}

// Star for one team (score card, team page).
export function TeamFollow({ sport = HQ.sport, teamId, teamName, className }) {
  if (teamId === null || teamId === undefined || teamId === "") return null;
  return <shq-follow type="team" class={className} sport={sport} team-id={String(teamId)} team-name={teamName || ""} />;
}

// Passive, non-interactive marker for list cards: shown when the game or one of its teams is followed.
// `marker=false` hides the star (inside the "Du följer" section, whose header already says it).
// `live` adds a text badge "Live" (never colour only) for a followed game that is being played.
export function FollowMarker({ match, sport = HQ.sport, live = false, marker = true }) {
  const attributes = gameAttributes(match, sport);
  if (!marker && !live) return null;
  return <span className="match-card__marker">
    {live && <span className="match-card__live"><span className="match-card__live-dot" aria-hidden="true" />Live</span>}
    {marker && <shq-follow-marker {...attributes} home-id={match?.home_team_id ?? ""} away-id={match?.away_team_id ?? ""} />}
  </span>;
}

// Re-render when anything followed changes (stars, other tabs, account sync).
export function useFollowVersion() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((value) => value + 1);
    window.addEventListener("sports-hq-follows-change", bump);
    return () => window.removeEventListener("sports-hq-follows-change", bump);
  }, []);
  return version;
}

// True when the user follows the game itself (teams are matched by the callers' own lists).
export const followsGame = (match, sport = HQ.sport) => {
  const follows = window.SportsHQFollows;
  const key = follows?.keyFromLink(match?.match_link, sport);
  return Boolean(key && follows.isGameFollowed(key));
};

// Public reactions stay separate from private follow state and list markers.
export function GameLike({ match, link, sport = HQ.sport }) {
  if (!match?.home_team || !match?.away_team) return null;
  return <shq-like class="hq-match-like" {...gameAttributes(match, sport)} link={link || match.match_link || ''} />;
}

// Provider event IDs can be array positions; a goal's cumulative score is stable under edits/reordering.
export function goalKey(event, matchKey) {
  const type = String(event?.type || '').toLowerCase();
  if (!['goal', 'penalty_goal'].includes(type) || event.counts_as_goal === false
      || /självmål|own.goal/i.test(`${event.label || ''} ${event.description || ''}`)) return null;
  const home = event.home_goals, away = event.away_goals;
  if (!Number.isInteger(home) || !Number.isInteger(away) || home < 0 || away < 0
      || home > 999 || away > 999 || home + away === 0 || !/^match:(floorball|hockey|football):[\w.-]+:[\w.-]+$/.test(matchKey || '')) return null;
  return `goal:${matchKey.slice(6)}:${home}-${away}`;
}

export function GoalLike({ event, match, link, sport = HQ.sport }) {
  const matchKey = window.SportsHQFollows?.keyFromLink(link || match?.match_link, sport);
  const key = goalKey(event, matchKey);
  if (!key) return null;
  const scorer = event.player_name || '';
  const assist = event.assist_name || event.assist_player_name || '';
  const label = `Gilla målet ${event.home_goals}–${event.away_goals}${scorer ? ` av ${scorer}` : ''}${assist ? `, assist ${assist}` : ''}`;
  return <shq-like kind="goal" game-key={key} reaction-label={label} />;
}
